<template>
  <main v-if="pageData" class="page">
    <div class="bg-dark">
      <div class="container">
        <header ref="header" class="header">
          <div class="header-text-wrapper">
            <h1 class="title" data-aos="fade-up">{{ pageData.clientName }}</h1>
            <p class="subtitle" data-aos="fade-up">{{ pageData.description }}</p>
          </div>
          <AppVideoPoster
            :image="pageData.caseStudiesPageImage.srcSet"
            :image-blur="pageData.caseStudiesPageImage.imageBlur"
            :subtitle="videoPosterContent"
            data-aos="fade-up"
            @play="openVideoPopup"
          />
        </header>
      </div>
    </div>

    <section class="section --case-study-info --with-wrapper">
      <div class="case-study-info" data-aos="fade-up">
        <div class="column">
          <p v-if="pageData.longDescription" class="name">{{ descriptionContent }}</p>
          <img v-if="clientLogo" v-lazy-load :data-src="clientLogo" class="logo" alt="" />
          <p v-if="pageData.longDescription" class="content">
            {{ pageData.longDescription }}
          </p>
        </div>
        <div v-if="pageData.deliverables" class="column" data-aos="fade-up">
          <p class="name">{{ deliverablesContent }}</p>
          <ul class="content-list">
            <li v-for="item in pageData.deliverables" :key="item.content" class="content-item">
              {{ item.content }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <div class="counters-wrapper">
      <LazyHydrate when-visible>
        <AppCounters />
      </LazyHydrate>
    </div>
    <LazyHydrate when-visible>
      <Slider class="slider" :images="images" :images-for-lightbox="imagesForLightbox" />
    </LazyHydrate>
    <LazyHydrate when-visible
      ><AppFeaturesGridSection
        v-if="pageData.features"
        class="app-features-grid --with-wrapper"
        :features="pageData.features.feature"
    /></LazyHydrate>

    <LazyHydrate when-visible
      ><AppFeaturesList
        v-if="pageData.featuresList"
        class="app-features-list"
        :features-list="pageData.featuresList"
    /></LazyHydrate>

    <LazyHydrate when-visible
      ><AppTestimonialsSection
        v-if="testimonialsData.length"
        class="app-testimonials --with-wrapper"
        :testimonials="testimonialsData"
    /></LazyHydrate>

    <div class="other-case-studies" :class="{ '--single': otherCaseStudies.length < 2 }">
      <router-link
        :to="{
          name: `${SINGLE_PORTFOLIO.slug}__${getCurrentLanguage}`,
          params: { slug: otherCaseStudies[0].slug },
        }"
        no-prefetch
        class="other-case-study --prev"
      >
        <div class="overlay"></div>
        <LazyHydrate when-visible
          ><AppImage
            class="img"
            :image="otherCaseStudies[0].caseStudyItem.general.caseStudiesBoxImage.srcSet"
            :alt="otherCaseStudies[0].caseStudyItem.general.caseStudiesBoxImage.altText"
            sizes="(max-width: 1200px) 100vw, 1200px"
            :blurred-preview="
              otherCaseStudies[0].caseStudyItem.general.caseStudiesBoxImage.imageBlur
            "
        /></LazyHydrate>
        <div class="content">
          <p class="title">{{ otherCaseStudies[0].caseStudyItem.general.clientName }}</p>
          <p v-if="pageData.changeCaseStudyButtons.previousButtonText" class="subtitle">
            <ArrowLinearIcon /><span class="--desktop">{{
              pageData.changeCaseStudyButtons.previousButtonText
            }}</span>
          </p>
        </div>
      </router-link>
      <router-link
        v-if="otherCaseStudies.length > 1"
        no-prefetch
        :to="{
          name: `${SINGLE_PORTFOLIO.slug}__${getCurrentLanguage}`,
          params: { slug: otherCaseStudies[1].slug },
        }"
        class="other-case-study --next"
      >
        <div class="overlay"></div>
        <LazyHydrate when-visible
          ><AppImage
            class="img"
            :image="otherCaseStudies[1].caseStudyItem.general.caseStudiesBoxImage.srcSet"
            :alt="otherCaseStudies[1].caseStudyItem.general.caseStudiesBoxImage.altText"
            sizes="(max-width: 1200px) 100vw, 1200px"
            :blurred-preview="
              otherCaseStudies[1].caseStudyItem.general.caseStudiesBoxImage.imageBlur
            "
        /></LazyHydrate>
        <div class="content">
          <p class="title">{{ otherCaseStudies[1].caseStudyItem.general.clientName }}</p>
          <p class="subtitle">
            <span class="--desktop">{{ pageData.changeCaseStudyButtons.nextButtonText }}</span
            ><ArrowLinearIcon />
          </p>
        </div>
      </router-link>
    </div>

    <LazyHydrate when-visible
      ><AppVideoPopup :show="showVideoPopup" @close="closeVideoPopup">
        <video
          class="popup-video"
          controls
          loop
          autoplay
          v-lazy-load :data-poster="pageData.caseStudiesPageImage.mediaItemUrl"
          muted
          v-lazy-load :data-src="videoUrl"
          type="video/mp4"
        ></video> </AppVideoPopup
    ></LazyHydrate>
  </main>
</template>

<script>
import LazyHydrate from 'vue-lazy-hydration'
import { ROUTES } from '@/utils/routes'
import AppVideoPoster from '@/components/common/AppVideoPoster'
import { getParsedSeoTags } from '@/utils/getParsedSeoTags.js'

export default {
  name: 'SinglePortfolioPage',
  components: {
    LazyHydrate,
    AppVideoPoster,
    Slider: () => import('@/components/single-portfolio/Slider'),
    AppVideoPopup: () => import('@/components/common/AppVideoPopup'),
    AppFeaturesGridSection: () => import('@/components/common/sections/AppFeaturesGridSection'),
    ArrowLinearIcon: () => import('@/components/icons/ArrowLinearIcon'),
    AppCounters: () => import('@/components/common/AppCounters'),
    AppImage: () => import('@/components/common/AppImage'),
    AppTestimonialsSection: () => import('@/components/common/sections/AppTestimonialsSection'),
    AppFeaturesList: () => import('@/components/common/AppFeaturesList'),
  },
  scrollToTop: false,
  async asyncData({ $cacheFetch, $graphql, route }) {
    const variables = { slug: route.params.slug }
    const lang = route.meta[0].lang
    return await $cacheFetch(
      { key: `${route.path}-${lang}-${route.meta[0].slug}`, expire: 99999 },
      async () => {
        let query
        if (lang === 'en') {
          const { EN } = await import('../graphql/SinglePortfolioQueries.graphql')
          query = EN
        } else if (lang === 'fr') {
          const { FR } = await import('../graphql/SinglePortfolioQueries.graphql')
          query = FR
        } else if (lang === 'es') {
          const { ES } = await import('../graphql/SinglePortfolioQueries.graphql')
          query = ES
        } else if (lang === 'de') {
          const { DE } = await import('../graphql/SinglePortfolioQueries.graphql')
          query = DE
        } else if (lang === 'pl') {
          const { PL } = await import('../graphql/SinglePortfolioQueries.graphql')
          query = PL
        } else {
          const { EN } = await import('../graphql/SinglePortfolioQueries.graphql')
          query = EN
        }
        const res = await $graphql.default.request(query, variables)
        const caseStudyBy = res.caseStudyBy
        const caseStudies = res.caseStudies
        return { caseStudyBy, caseStudies, variables, lang }
      }
    )
  },
  data() {
    return {
      index: null,
      caseStudyBy: null,
      showVideoPopup: false,
      SINGLE_PORTFOLIO: ROUTES.SINGLE_PORTFOLIO,
      dataReady: false,
    }
  },
  head() {
    if (this.pageData) {
      return {
        ...getParsedSeoTags(this.caseStudyBy?.seo?.fullHead, this.$route),
        title: `${this.pageData?.clientName} portfolio - Resimo - Interactive 3D sales application for Real Estate`,
        meta: [
          {
            hid: 'description',
            name: 'description',
            content:
              'Show your clients what their apartment will look like by using an interactive investment presentation! Use the application and let clients see the interior of the flat and view from the window or allow them to explore the area.',
          },
        ],
        htmlAttrs: {
          lang: this.lang,
        },
        link: [
          {
            rel: 'canonical',
            href: `${process.env.ORIGIN_URL}${this.$route.path}`,
          },
          {
            rel: 'preload',
            as: 'image',
            imagesrcset: this.caseStudyBy.caseStudyItem.general.caseStudiesPageImage.srcSet,
          },
        ],
      }
    }
  },
  computed: {
    getCurrentLanguage() {
      return this.$store.getters.getCurrentLanguage
    },
    pageData() {
      if (this.caseStudyBy?.caseStudyItem?.[this.getCurrentLanguage]) {
        return {
          ...this.caseStudyBy.caseStudyItem[this.getCurrentLanguage],
          ...this.caseStudyBy.caseStudyItem.general,
        }
      }
      return null
    },
    testimonialsData() {
      if (this.pageData.testimonials) {
        return this.pageData.testimonials.map((item) => ({
          image: item.testimonials.image,
          ...item.testimonials[this.getCurrentLanguage],
        }))
      }
      return {}
    },
    images() {
      if (this.pageData.slider.slide) {
        return this.pageData.slider.slide.map((slide) => {
          return {
            srcset: slide.image.srcSet,
            blurred: slide.image.imageBlur,
            altText: slide.image.altText,
            mediaItemUrl: slide.image.mediaItemUrl,
          }
        })
      }
      return []
    },
    imagesForLightbox() {
      if (this.pageData.slider.slide) {
        return this.pageData.slider.slide.map((slide) => {
          return {
            src: slide.image.mediaItemUrl,
            width: 1920,
            height: 1080,
          }
        })
      }
      return []
    },
    otherCaseStudies() {
      if (this.caseStudies?.nodes) {
        const currentIndex = this.caseStudies.nodes.findIndex(
          (item) => item.slug === this.variables.slug
        )
        let prevItem
        let nextItem

        if (currentIndex !== 0 && currentIndex !== this.caseStudies.nodes.length - 1) {
          prevItem = this.caseStudies.nodes[currentIndex - 1]
          nextItem = this.caseStudies.nodes[currentIndex + 1]
        }

        if (currentIndex === 0) {
          prevItem = this.caseStudies.nodes[this.caseStudies.nodes.length - 1]
          nextItem = this.caseStudies.nodes[currentIndex + 1]
        }

        if (currentIndex === this.caseStudies.nodes.length - 1) {
          prevItem = this.caseStudies.nodes[currentIndex - 1]
          nextItem = this.caseStudies.nodes[0]
        }

        return [prevItem, nextItem]
      }

      return []
    },
    clientLogo() {
      if (this.pageData.logo) {
        return this.pageData?.logo[0]?.clients?.client?.logo?.mediaItemUrl
      }
      return null
    },
    videoUrl() {
      if (this?.pageData?.caseStudiesPageVideoUrl) return this.pageData.caseStudiesPageVideoUrl
      if (this.pageData?.caseStudiesPageVideo?.mediaItemUrl)
        return this.pageData.caseStudiesPageVideo.mediaItemUrl
      return ''
    },

    deliverablesContent() {
      if (this.lang === 'es') return 'Resultados'
      if (this.lang === 'fr') return 'Résultats'
      else return 'Deliverables'
    },

    descriptionContent() {
      if (this.lang === 'es') return 'Descripción'
      if (this.lang === 'fr') return 'Description'
      else return 'Description'
    },

    videoPosterContent() {
      if (this.lang === 'es') return 'Vea cómo funciona la aplicación'
      if (this.lang === 'fr') return `Voir comment fonctionne l'application`
      else return 'See how the application works'
    },
  },
  watch: {
    getCurrentLanguage() {
      this.getData()
    },
  },
  beforeMount() {
    window.scrollTo(0, 0)
  },
  mounted() {
    this.$nextTick(() => {
      this.$nuxt.$loading.finish(true)
    })
  },
  methods: {
    closeVideoPopup() {
      this.showVideoPopup = false
    },
    openVideoPopup() {
      this.showVideoPopup = true
    },
    async getData() {
      let query
      if (this.getCurrentLanguage === 'en') {
        const { EN } = await import('../graphql/SinglePortfolioQueries.graphql')
        query = EN
      } else if (this.getCurrentLanguage === 'fr') {
        const { FR } = await import('../graphql/SinglePortfolioQueries.graphql')
        query = FR
      } else if (this.getCurrentLanguage === 'es') {
        const { ES } = await import('../graphql/SinglePortfolioQueries.graphql')
        query = ES
      } else if (this.getCurrentLanguage === 'de') {
        const { DE } = await import('../graphql/SinglePortfolioQueries.graphql')
        query = DE
      } else if (this.getCurrentLanguage === 'pl') {
        const { PL } = await import('../graphql/SinglePortfolioQueries.graphql')
        query = PL
      } else {
        const { EN } = await import('../graphql/SinglePortfolioQueries.graphql')
        query = EN
      }

      const res = await this.$graphql.default.request(query, this.variables)

      const caseStudyBy = res.caseStudyBy
      const caseStudies = res.caseStudies
      this.caseStudyBy = caseStudyBy
      this.caseStudies = caseStudies
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/pages/SinglePortfolio';
</style>
